//***************************************************
// InfoSend_Agora.tsx
// テレビ会議の作成（今すぐ開始のみ）
//***************************************************
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Typography, Table, message, Button, Modal, Input, Radio, Card, Space, Tag } from "antd";
import type { RadioChangeEvent } from "antd";
import { doc, getDoc, setDoc, collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { Device, Agora } from "./web_parts/types";
import DeviceListModule from "./web_parts/DeviceList_module";
import axios from "axios";
import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import "../../styles/global.css";
import { useAuth } from "../../context/AuthContext";
import { ja } from "date-fns/locale";

const { Title } = Typography;
const FIXED_DURATION_MINUTES = 30;

/** 開設済み参加時の表示名: ブラウザにキャッシュ（ユーザー別） */
const joinParticipantNameStorageKey = (uid: string) => `caretablet:agoraJoinParticipantDisplayName:${uid}`;

function readCachedJoinParticipantName(uid: string | undefined): string {
  if (!uid || typeof window === "undefined") return "";
  try {
    const v = window.localStorage.getItem(joinParticipantNameStorageKey(uid));
    return v != null ? v.trim() : "";
  } catch {
    return "";
  }
}

function writeCachedJoinParticipantName(uid: string | undefined, name: string): void {
  if (!uid || typeof window === "undefined" || !name.trim()) return;
  try {
    window.localStorage.setItem(joinParticipantNameStorageKey(uid), name.trim());
  } catch {
    /* ignore quota / private mode */
  }
}

export interface InfoSend_AgoraProps {
  /** 単一デバイス指定時（デバイス操作画面から呼び出し）はモーダルのみ表示 */
  singleDevice?: Device;
  onClose?: () => void;
}

const InfoSend_Agora: React.FC<InfoSend_AgoraProps> = ({ singleDevice, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingMeeting, setIsSendingMeeting] = useState(false);
  const sendInProgressRef = useRef(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(singleDevice ?? null);
  const [selectedDeviceList, setSelectedDeviceList] = useState<Device[]>(singleDevice ? [singleDevice] : []);
  const [isDialogVisible, setIsDialogVisible] = useState(!!singleDevice);

  const [agoraData, setAgoraData] = useState<Agora[]>([]);
  const [duration] = useState(FIXED_DURATION_MINUTES);
  const { currentUser } = useAuth();
  const [holderName, setHolderName] = useState("");
  const [originalHolderName, setOriginalHolderName] = useState("");
  const [CheckAuto, setCheckAuto] = useState(0);
  const resolvedHostName = holderName.trim() || originalHolderName.trim() || "開催者";
  const meetingName = `${resolvedHostName}さん開催 のテレビ会議`;

  /** 開設済み会議の情報（closedAt未設定＝タブレット退室前） */
  const [existingMeeting, setExistingMeeting] = useState<{
    channelID: string;
    meetingName: string;
    starttime: number;
    durationminute: number;
    regPerson: string;
  } | null>(null);
  /** ダイアログモード: 'new'=新規作成, 'joinExisting'=開設済みに参加 */
  const [dialogMode, setDialogMode] = useState<"new" | "joinExisting">("new");
  /** 開設済み参加時の表示名（入力のみ） */
  const [joinParticipantName, setJoinParticipantName] = useState("");
  /** 開設済み検出時のサブモード: 'join'=参加（デフォルト）, 'new'=新規開催（意図的に切り替えた場合のみ） */
  const [existingMeetingSubMode, setExistingMeetingSubMode] = useState<"join" | "new">("join");
  const isJoinExistingSelected = dialogMode === "joinExisting" && existingMeetingSubMode === "join";

  /** 参加名: localStorage → loginusers.displayName → 開催者欄など */
  const resolveJoinParticipantName = useCallback(
    async (holder: string, original: string): Promise<string> => {
      const uid = currentUser?.uid;
      const cached = readCachedJoinParticipantName(uid);
      if (cached) return cached;
      if (uid) {
        try {
          const snap = await getDoc(doc(db, "loginusers", uid));
          if (snap.exists()) {
            const d = snap.data() as Record<string, unknown>;
            const dn = String(d.displayName ?? d.userName ?? "").trim();
            if (dn) return dn;
          }
        } catch (e) {
          console.warn("loginusers の displayName 取得に失敗", e);
        }
      }
      const fromHolder = (holder || original || "").trim();
      if (fromHolder) return fromHolder;
      return (currentUser?.email?.split("@")[0] || "").trim();
    },
    [currentUser?.uid, currentUser?.email]
  );

  const AgoraColumns = [
    { title: "会議名", dataIndex: "meetingName", key: "meetingName" },
    { title: "開始時間", dataIndex: "starttime", key: "starttime" },
    { title: "会議の時間", dataIndex: "durationminute", key: "durationminute" },
    { title: "チャネルID", dataIndex: "channelID", key: "channelID" },
    { title: "登録者", dataIndex: "regPerson", key: "regPerson" },
    { title: "設定の更新日時", dataIndex: "updateTime", key: "updateTime" },
  ];

  /** 開設済み会議（closedAt が未設定）を検出。タブレット退室時に closedAt が記録されるため、未設定＝進行中と判定 */
  const checkOngoingMeeting = async (): Promise<{
    channelID: string;
    meetingName: string;
    starttime: number;
    durationminute: number;
    regPerson: string;
  } | null> => {
    const androidDevices = selectedDeviceList.filter((d) => d.serialNumber?.length > 0);
    if (androidDevices.length === 0) return null;

    for (const device of androidDevices) {
      const q = query(
        collection(db, "agoraitems"),
        where("serialNumber", "==", device.serialNumber!),
        orderBy("updateTime", "desc")
      );
      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        const d = docSnap.data();
        if (d.closedAt !== undefined) continue; // 退室済み（タブレットが closedAt を記録済み）
        const start = Number(d.starttime || 0);
        const dur = Number(d.durationminute || 30) * 60 * 1000;
        if (start + dur < Date.now()) continue; // 終了時刻を過ぎている
        return {
          channelID: d.channelID || "",
          meetingName: d.meetingName || "",
          starttime: start,
          durationminute: Number(d.durationminute || 30),
          regPerson: d.regPerson || "",
        };
      }
    }
    return null;
  };

  const fetchAgoraData = async (serialNumber: string) => {
    setIsLoading(true);
    try {
      const agoraQuery = query(
        collection(db, "agoraitems"),
        where("serialNumber", "==", serialNumber),
        orderBy("starttime", "desc")
      );
      const querySnapshot = await getDocs(agoraQuery);
      const data: Agora[] = querySnapshot.docs.map((docSnap) => {
        const docData = docSnap.data();
        return {
          id: docData.id || "",
          meetingName: docData.meetingName || "",
          starttime: docData.starttime ? format(new Date(Number(docData.starttime)), "yyyy年MM月dd日 HH:mm", { locale: ja }) : "",
          durationminute: docData.durationminute || 0,
          channelID: docData.channelID || "",
          name: docData.name || "",
          uid: docData.uid || 0,
          deleted: docData.deleted || false,
          updateTime: docData.updateTime ? format(new Date(Number(docData.updateTime)), "yyyy年MM月dd日 HH:mm", { locale: ja }) : "",
          serialNumber: docData.serialNumber || "",
          regPerson: docData.regPerson || "",
          ictabUid: docData.ictabUid || "",
        };
      });
      setAgoraData(data);
      message.success("対象デバイスに設定されたテレビ会議のスケジュールを表示しました。");
    } catch (error) {
      message.error("テレビ会議のデータ取得中にエラーが発生しました。");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  /** singleDevice モードでモーダルが最初から開く場合、開設済み会議をチェック */
  useEffect(() => {
    if (!singleDevice || selectedDeviceList.length === 0) return;
    let cancelled = false;
    const run = async () => {
      const ongoing = await checkOngoingMeeting();
      if (cancelled) return;
      if (ongoing) {
        setExistingMeeting(ongoing);
        setDialogMode("joinExisting");
        setExistingMeetingSubMode("join");
        const name = await resolveJoinParticipantName(holderName, originalHolderName);
        if (!cancelled) setJoinParticipantName(name);
      } else {
        setDialogMode("new");
        setExistingMeeting(null);
      }
    };
    run().catch(() => {
      if (!cancelled) {
        setDialogMode("new");
        setExistingMeeting(null);
      }
    });
    return () => { cancelled = true; };
  }, [singleDevice?.serialNumber, selectedDeviceList?.length, resolveJoinParticipantName]);

  useEffect(() => {
    const fetchUserName = async () => {
      if (currentUser) {
        try {
          const userEmail = (currentUser.email ?? "").trim().toLowerCase();
          const userDoc = await getDocs(query(collection(db, "loginusers"), where("email", "==", userEmail)));
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            const displayName = userData.displayName || currentUser.email?.split("@")[0] || "開催者";
            setHolderName(displayName);
            setOriginalHolderName(displayName);
          } else {
            const fallbackName = currentUser.email?.split("@")[0] || "開催者";
            setHolderName(fallbackName);
            setOriginalHolderName(fallbackName);
          }
        } catch (error) {
          console.error("ユーザー名取得エラー:", error);
          setHolderName("開催者");
          setOriginalHolderName("開催者");
        }
      } else {
        setHolderName("開催者");
        setOriginalHolderName("開催者");
      }
    };
    fetchUserName();
  }, [currentUser]);

  /** 開設済み参加モードで名前欄が空のとき、キャッシュまたは loginusers を再適用（開催者名の読込タイミング差を吸収） */
  useEffect(() => {
    if (dialogMode !== "joinExisting" || existingMeetingSubMode !== "join" || !isDialogVisible) return;
    if (joinParticipantName.trim().length > 0) return;
    let cancelled = false;
    void (async () => {
      const name = await resolveJoinParticipantName(holderName, originalHolderName);
      if (!cancelled && name) setJoinParticipantName(name);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    dialogMode,
    existingMeetingSubMode,
    isDialogVisible,
    holderName,
    originalHolderName,
    joinParticipantName,
    resolveJoinParticipantName,
  ]);

  const handleDeviceSelected = (device: Device) => {
    setSelectedDevice(device);
    if (device.serialNumber) fetchAgoraData(device.serialNumber);
  };

  const handleDevicesSelected = (devices: Device[]) => setSelectedDeviceList(devices);

  const handleSendDlgOpen = async () => {
    if (!selectedDeviceList || selectedDeviceList.length === 0) {
      message.warning("テレビ会議の予定を登録する対象のデバイスを選択してください。");
      return;
    }
    setIsLoading(true);
    try {
      const ongoing = await checkOngoingMeeting();
      if (ongoing) {
        setExistingMeeting(ongoing);
        setDialogMode("joinExisting");
        setExistingMeetingSubMode("join");
        const name = await resolveJoinParticipantName(holderName, originalHolderName);
        setJoinParticipantName(name);
      } else {
        setDialogMode("new");
        setExistingMeeting(null);
      }
      setIsDialogVisible(true);
    } catch (e) {
      console.error("開設済み会議の検出エラー:", e);
      setDialogMode("new");
      setExistingMeeting(null);
      setIsDialogVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isSendingMeeting) {
      message.warning("テレビ会議の呼び出し処理中です。ページを閉じずにお待ちください。");
      return;
    }
    setIsDialogVisible(false);
    setDialogMode("new");
    setExistingMeeting(null);
    setExistingMeetingSubMode("join");
    onClose?.();
  };

  /** 開設済み会議に参加する（FCMは送らない。Webから直接参加） */
  const handleJoinExistingMeeting = () => {
    if (!existingMeeting?.channelID) return;
    const nameToUse = joinParticipantName.trim();
    if (!nameToUse) {
      message.error("参加する名前を入力してください。");
      return;
    }
    writeCachedJoinParticipantName(currentUser?.uid, nameToUse);
    const url = `${window.location.origin}${window.location.pathname}#/standard-meeting/${existingMeeting.channelID}?invited=true&name=${encodeURIComponent(nameToUse)}`;
    window.open(url, "_blank");
    message.success("会議に参加する画面を開きました。タブレットには呼び出しは送信されません。");
    handleCancel();
  };

  const handleAutoConnectChange = (event: RadioChangeEvent) => {
    setCheckAuto(event.target.value === 1 ? 1 : 0);
  };

  const handleSendMessage = async () => {
    if (sendInProgressRef.current || isSendingMeeting) {
      message.warning("テレビ会議の呼び出し処理中です。完了までお待ちください。");
      return;
    }

    sendInProgressRef.current = true;
    setIsSendingMeeting(true);
    message.loading({
      key: "agora-meeting-start",
      content: "テレビ会議の接続開始には20～30秒ほどかかる場合があります。ページを閉じずにお待ちください。",
      duration: 0,
    });

    try {
      const androidDevices = selectedDeviceList.filter((d) => d.serialNumber?.length > 0);
      if (androidDevices.length === 0) {
        message.warning("選択された端末にAndroid端末が含まれていません。");
        return;
      }

      const tokens = androidDevices.map((d) => d.fcmToken).filter(Boolean) as string[];
      if (tokens.length === 0) {
        message.error("選択されたデバイスにトークンが設定されていません。");
        return;
      }

      if (!holderName.trim()) {
        message.error("開催者の名前を入力してください。");
        return;
      }
      if (!originalHolderName.trim()) {
        message.error("ユーザー情報の取得に失敗しました。再ログインしてください。");
        return;
      }
      if (!meetingName || !duration) {
        message.error("入力されていない項目があります。ご確認ください。");
        return;
      }

      const nowtime = Date.now();
      const channelId = uuidv4();
      const sz_updateTime = Date.now().toString();

      const payload = {
        mode: "request",
        command: "start_agora",
        // Android の呼び出し表示は user を使用するため、表示名を優先して送る
        user: holderName.trim() || originalHolderName,
        place: "CENTER",
        param1: String(channelId),
        param2: String(meetingName),
        param3: currentUser?.uid || "",
        param4: String(duration),
        param5: String(nowtime),
        param6: sz_updateTime,
        param7: String(CheckAuto),
        // 予備: 表示名が空の場合のフォールバック用にログインIDを保持
        param8: originalHolderName,
      };

      const response = await axios.post("/api/sendFCM", { tokens, payload }, { headers: { "Content-Type": "application/json" } });
      if (response.status !== 200) {
        message.error(`FCMメッセージの送信に失敗しました: ${response.data.error}`);
        return;
      }

      message.success("選択されたAndroid端末にテレビ会議の予定を送信しました。");

      const successfulDevices = (response.data.results || [])
        .filter((r: { success: boolean }) => r.success)
        .map((r: { token: string }) => r.token);

      let newtokens: string[] = [];
      let newpayloads: { [token: string]: { command: string; docid: string; updatetime: string } } = {};

      // 主催者の分のデータ
      const agoraQuery = query(collection(db, "agoraitems"), where("serialNumber", "==", "00000000"));
      const querySnapshot = await getDocs(agoraQuery);
      const existingDocs = querySnapshot.docs.map((d) => d.id);
      const maxIndex = existingDocs
        .map((id) => parseInt(id.split("_")[1], 10))
        .filter((num) => !isNaN(num))
        .reduce((max, num) => Math.max(max, num), 0);
      const newDocIdHost = `00000000_${maxIndex + 1}`;

      await setDoc(doc(db, "agoraitems", newDocIdHost), {
        meetingName,
        starttime: nowtime,
        durationminute: duration,
        channelID: channelId,
        updateTime: Number(sz_updateTime),
        regPerson: originalHolderName,
        serialNumber: "00000000",
        name: holderName,
        uid: 99999999,
        deleted: false,
        ictabUid: currentUser?.uid || "",
      });

      let cnt = 0;
      for (const device of androidDevices) {
        const q = query(collection(db, "agoraitems"), where("channelID", "==", channelId), where("serialNumber", "==", device.serialNumber));
        const snapshot = await getDocs(q);
        if (snapshot.empty && successfulDevices.includes(device.fcmToken!)) {
          const serialNumber = device.serialNumber!;
          const devQuery = query(collection(db, "agoraitems"), where("serialNumber", "==", serialNumber));
          const devSnapshot = await getDocs(devQuery);
          const devDocs = devSnapshot.docs.map((d) => d.id);
          const devMaxIndex = devDocs
            .map((id) => parseInt(id.split("_")[1], 10))
            .filter((num) => !isNaN(num))
            .reduce((max, num) => Math.max(max, num), 0);
          const newDocId = `${serialNumber}_${devMaxIndex + 1}`;

          await setDoc(doc(db, "agoraitems", newDocId), {
            meetingName,
            starttime: nowtime,
            durationminute: duration,
            channelID: channelId,
            updateTime: Number(sz_updateTime),
            // 会議作成時点のタブレット利用者名を agoraitems に保持（UID 同期前の表示用）。
            regPerson: originalHolderName,
            serialNumber,
            name: (device.userDisplayName ?? "").trim(),
            uid: 0,
            deleted: false,
            ictabUid: currentUser?.uid || "",
          });

          newtokens.push(device.fcmToken!);
          newpayloads[device.fcmToken!] = { command: "agora", docid: newDocId, updatetime: sz_updateTime };
          cnt++;
        }
      }

      if (cnt > 0) {
        await axios.post("/api/sendFCMforIDUpdate", { newtokens, newpayloads }, { headers: { "Content-Type": "application/json" } });
      }

      const agoraWebUrl = `${window.location.origin}${window.location.pathname}#/standard-meeting/${channelId}`;
      window.open(agoraWebUrl, "_blank");
    } catch (error) {
      message.error("FCMメッセージの送信中にエラーが発生しました。");
      console.error("FCMメッセージの送信中にエラーが発生しました。", error);
      return;
    } finally {
      sendInProgressRef.current = false;
      setIsSendingMeeting(false);
      message.destroy("agora-meeting-start");
    }
    setIsDialogVisible(false);
    onClose?.();
  };

  const isSingleDeviceMode = !!singleDevice && !!onClose;

  return (
    <div>
      {!isSingleDeviceMode && (
        <>
          <DeviceListModule onDeviceSelected={handleDeviceSelected} onDevicesSelected={handleDevicesSelected} />
          <div style={{ marginBottom: 16 }}>
            <Button type="primary" onClick={handleSendDlgOpen}>
              今すぐテレビ会議を開始する
            </Button>
            （チェックボックスで選択したデバイスを呼び出してテレビ会議を作成します。）
          </div>
          <Table
            columns={AgoraColumns}
            dataSource={agoraData}
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 10 }}
            title={() => (
              <div>
                <Title level={5}>選択したデバイスに登録されているテレビ会議の一覧：シリアル番号：<strong>{selectedDevice?.serialNumber || "未選択"}</strong></Title>
              </div>
            )}
          />
        </>
      )}

      <Modal
        title={
          dialogMode === "joinExisting"
            ? existingMeetingSubMode === "new"
              ? "新規会議を開始する"
              : "開設済みの会議に参加する"
            : "今すぐテレビ会議を開始する"
        }
        open={isDialogVisible}
        onCancel={handleCancel}
        closable={!isSendingMeeting}
        maskClosable={!isSendingMeeting}
        keyboard={!isSendingMeeting}
        footer={null}
        width={760}
      >
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Card
            size="small"
            title="呼び出し先デバイス"
            style={{ borderRadius: 8 }}
            styles={{ body: { background: "#fafafa" } }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "170px 1fr", rowGap: 10, columnGap: 8, alignItems: "center" }}>
              <div style={{ color: "#8c8c8c", fontWeight: 500 }}>デバイス名</div>
              <div style={{ fontWeight: 600 }}>{selectedDeviceList[0]?.deviceName || "—"}</div>
              <div style={{ color: "#8c8c8c", fontWeight: 500 }}>Androidユーザー名</div>
              <div style={{ fontWeight: 600 }}>{selectedDeviceList[0]?.userDisplayName || "ユーザー名未登録"}</div>
            </div>
          </Card>
        </Space>

        {dialogMode === "joinExisting" && existingMeeting ? (
          <>
            <Card size="small" title="操作を選択" style={{ borderRadius: 8, marginTop: 12 }}>
              <Radio.Group
                value={existingMeetingSubMode}
                onChange={(e) => {
                  const v = e.target.value as "join" | "new";
                  setExistingMeetingSubMode(v);
                  if (v === "join") {
                    void (async () => {
                      const name = await resolveJoinParticipantName(holderName, originalHolderName);
                      setJoinParticipantName((prev) => (prev.trim().length > 0 ? prev : name));
                    })();
                  }
                }}
                optionType="button"
                buttonStyle="solid"
                options={[
                  { label: "同じ会議に参加", value: "join" },
                  { label: "新規に開催", value: "new" },
                ]}
              />
            </Card>
            {!isJoinExistingSelected && (
              <Card
                size="small"
                title="接続方法"
                style={{ borderRadius: 8, marginTop: 12 }}
              >
                <Radio.Group value={CheckAuto} onChange={handleAutoConnectChange}>
                  <Space direction="vertical" size={4}>
                    <Radio value={0}>CareTabletの操作による許可後に接続する</Radio>
                    <Radio value={1}>CareTabletでの操作なしに自動接続する</Radio>
                  </Space>
                </Radio.Group>
              </Card>
            )}
            {existingMeetingSubMode === "join" ? (
              <>
                <Card size="small" title="開設済み会議の情報" style={{ borderRadius: 8, marginTop: 12 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", rowGap: 8, columnGap: 8 }}>
                    <div style={{ color: "#8c8c8c" }}>会議名</div>
                    <div>{existingMeeting.meetingName}</div>
                    <div style={{ color: "#8c8c8c" }}>開始時間</div>
                    <div>{format(new Date(existingMeeting.starttime), "yyyy年MM月dd日 HH:mm", { locale: ja })}</div>
                    <div style={{ color: "#8c8c8c" }}>会議時間</div>
                    <div>{existingMeeting.durationminute} 分</div>
                    <div style={{ color: "#8c8c8c" }}>開催者</div>
                    <div>{existingMeeting.regPerson}</div>
                  </div>
                </Card>
                <Card size="small" title="参加する名前" style={{ borderRadius: 8, marginTop: 12 }}>
                  <Input
                    value={joinParticipantName}
                    style={{ maxWidth: 320 }}
                    onChange={(e) => setJoinParticipantName(e.target.value)}
                    placeholder="あなたの名前"
                  />
                </Card>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                  <Button onClick={handleCancel} disabled={isSendingMeeting}>キャンセル</Button>
                  <Button type="primary" onClick={handleJoinExistingMeeting} disabled={!joinParticipantName.trim()}>
                    同じ会議に参加する
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Card size="small" title="新規会議の設定内容" style={{ borderRadius: 8, marginTop: 12 }}>
                  <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <div>
                      <div style={{ color: "#595959", marginBottom: 6 }}>会議の名称</div>
                      <Input value={meetingName} style={{ maxWidth: 420 }} disabled />
                    </div>
                    <div>
                      <div style={{ color: "#595959", marginBottom: 6 }}>開催者の名前</div>
                      <Input
                        value={holderName}
                        style={{ maxWidth: 320 }}
                        onChange={(e) => setHolderName(e.target.value)}
                        placeholder="開催者の名前"
                      />
                    </div>
                    <div>
                      <div style={{ color: "#595959", marginBottom: 6 }}>会議時間</div>
                      <Tag color="blue">最大30分</Tag>
                    </div>
                  </Space>
                </Card>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                  <Button onClick={handleCancel} style={{ marginRight: 8 }} disabled={isSendingMeeting}>キャンセル</Button>
                  <Button type="primary" onClick={handleSendMessage} loading={isSendingMeeting} disabled={isSendingMeeting}>テレビ会議を開催して呼び出す</Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <Card
              size="small"
              title="接続方法"
              style={{ borderRadius: 8, marginTop: 12 }}
            >
              <Radio.Group value={CheckAuto} onChange={handleAutoConnectChange}>
                <Space direction="vertical" size={4}>
                  <Radio value={0}>相手の画面操作による許可後に接続する</Radio>
                  <Radio value={1}>相手の許可なく自動的に接続する</Radio>
                </Space>
              </Radio.Group>
            </Card>
            <Card size="small" title="会議設定" style={{ borderRadius: 8, marginTop: 12 }}>
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <div>
                  <div style={{ color: "#595959", marginBottom: 6 }}>会議の名称</div>
                  <Input value={meetingName} style={{ maxWidth: 420 }} disabled />
                </div>
                <div>
                  <div style={{ color: "#595959", marginBottom: 6 }}>開催者の名前</div>
                  <Input
                    value={holderName}
                    style={{ maxWidth: 320 }}
                    onChange={(e) => setHolderName(e.target.value)}
                    placeholder="開催者の名前"
                  />
                </div>
                <div>
                  <div style={{ color: "#595959", marginBottom: 6 }}>会議時間</div>
                  <Tag color="blue">最大30分（固定）</Tag>
                </div>
              </Space>
            </Card>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <Button onClick={handleCancel} style={{ marginRight: 8 }} disabled={isSendingMeeting}>キャンセル</Button>
              <Button type="primary" onClick={handleSendMessage} loading={isSendingMeeting} disabled={isSendingMeeting}>テレビ会議を開催して呼び出す</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default InfoSend_Agora;
